import {
  CapabilityPage,
  type CapabilitySection,
} from "@/components/marketing/CapabilityPage";

/**
 * ``/features/firemni-zakaznici`` — the layer that keeps a regular trade
 * customer, written for the shop owner rather than for their buyer.
 *
 * ``/for-business`` already sells the same ground to the other side of
 * the desk ("Pro nákupní oddělení", "Vytvořit B2B tým"). Two audiences,
 * not a duplicate: here the subject is your customer, there it is the
 * reader's own purchasing.
 *
 * Every item is gated, which is unusual in this section — ``b2b`` and
 * ``b2b.approval`` from Pro, ``b2b.rules`` and ``capacity_contracts``
 * from Business, all read live. Only the two tracking items are free,
 * because ``/track`` is a per-tenant page toggle and no plan key.
 *
 * The ink block sits on pricing, not at the end: the money is the part
 * a shop owner weighs, and putting it third stops the page reading as
 * four identical grids.
 */
const SECTIONS: readonly CapabilitySection[] = [
  {
    id: "portal",
    items: [
      // The lede: a buyer seeing their colleagues' orders is the whole
      // reason a company stops ordering by e-mail.
      { id: "dashboard", entitlement: "b2b", wide: true },
      { id: "invoices", entitlement: "b2b" },
      { id: "reports", entitlement: "b2b" },
      { id: "addressBook", entitlement: "b2b" },
    ],
    shot: "b2b-portal",
  },
  {
    id: "control",
    items: [
      { id: "orgs", entitlement: "b2b" },
      { id: "approvals", entitlement: "b2b.approval" },
      { id: "templates", entitlement: "b2b" },
      { id: "terms", entitlement: "b2b" },
    ],
    shot: "b2b-control",
  },
  {
    id: "pricing",
    tone: "ink",
    items: [
      { id: "rules", entitlement: "b2b.rules" },
      { id: "prebuy", entitlement: "capacity_contracts" },
    ],
    shot: "b2b-pricing",
  },
  { id: "visibility", items: [{ id: "track" }, { id: "honest" }], shot: "b2b-visibility" },
];

const PAINS = ["email", "report", "discount"] as const;
const GAINS = ["selfserve", "price", "paperwork"] as const;

export default function FeaturesB2b(): JSX.Element {
  return (
    <CapabilityPage
      i18nKey="features.pages.b2b"
      path="/features/firemni-zakaznici"
      pains={PAINS}
      gains={GAINS}
      sections={SECTIONS}
    />
  );
}
